import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { caisseAPI } from '../../services/api';
import Filter from '../../components/ui/Filter';
import { getRoleOptions } from '../../utils/roleUtils';

const UserFilters = ({ filters, onFiltersChange, onReset }) => {
  const [caisses, setCaisses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      setLoading(true);
      setError(null);
      try {
        const [caissesRes] = await Promise.all([
          caisseAPI.getForFilters()
        ]);
        setCaisses(Array.isArray(caissesRes.data) ? caissesRes.data : []);
      } catch (error) {
        console.error('Error loading filter options:', error);
        setError('Erreur lors du chargement des filtres');
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, []);

  // Handle individual filter changes
  const handleFilterChange = (filterName, value) => {
    let updatedFilters = {
      ...filters,
      [filterName]: value
    };

    onFiltersChange(updatedFilters);
  };

  // Define filter configuration
  const filterOptions = [
    {
      name: 'search',
      type: 'search',
      label: 'Rechercher',
      placeholder: 'Rechercher par nom d\'utilisateur, prénom, nom...',
      isMultiSelect: false
    },
    {
      name: 'role',
      type: 'dropdown',
      label: 'Rôle',
      placeholder: 'Rôle: Tous',
      isMultiSelect: false,
      isSearchable: false,
      options: getRoleOptions()
    },
    {
      name: 'caisseId',
      type: 'dropdown',
      label: 'Caisse',
      placeholder: 'Caisse: Tous',
      isMultiSelect: false,
      isSearchable: false,
      highlightOptionValues: ['ALL_MOBILE', 'ALL_PHYSIQUE'],
      options: Array.isArray(caisses) ? (() => {
        const physiques = caisses
          .filter(c => c.type === 'PHYSIQUE')
          .map(caisse => ({
            id: caisse.id,
            value: caisse.id,
            label: caisse.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        const mobiles = caisses
          .filter(c => c.type === 'MOBILE')
          .map(caisse => ({
            id: caisse.id,
            value: caisse.id,
            label: caisse.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        return [
          { id: 'ALL_MOBILE', value: 'ALL_MOBILE', label: 'Toutes les caisses mobiles' },
          { id: 'ALL_PHYSIQUE', value: 'ALL_PHYSIQUE', label: 'Toutes les caisses physiques' },
          ...physiques,
          ...mobiles
        ];
      })() : []
    },
    {
      name: 'active',
      type: 'dropdown',
      label: 'Statut',
      placeholder: 'Statut: Tous',
      isMultiSelect: false,
      isSearchable: false,
      options: [
        { value: 'true', label: 'Actif' },
        { value: 'false', label: 'Inactif' }
      ]
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 0 }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Filter
        filters={filterOptions}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onReset={onReset}
        showApplyButton={false}
      />
    </Box>
  );
};

export default UserFilters;
